/**
 * @module services/pictureInPicture
 *
 * Thin, video-element-agnostic wrappers around the browser Picture-in-Picture
 * APIs. Extracted out of MediaPlayer.vue so both the playlist player and the
 * standalone quick-play dialog (useEpisodePlayer.js / EpisodePlayerDialog.vue)
 * share the exact same cross-browser handling instead of drifting apart.
 *
 * Two APIs are supported:
 *   - The standard Picture-in-Picture Web API (Chrome, Firefox, Android).
 *   - iOS Safari/WebKit's older `webkitSetPresentationMode` API (also used by
 *     standalone/installed PWAs on iOS, which don't support the standard API).
 */

/**
 * Whether the browser can show Picture-in-Picture for the given video element.
 */
export const supportsPiP = (videoEl) => {
  if (!videoEl) return false;
  if (document.pictureInPictureEnabled) return true;
  if (videoEl.webkitSupportsPresentationMode) {
    return videoEl.webkitSupportsPresentationMode("picture-in-picture");
  }
  return false;
};

/** Whether the given video element is the one currently in PiP, under either API. */
export const isPiPActive = (videoEl) => {
  if (!videoEl) return false;
  if (document.pictureInPictureElement === videoEl) return true;
  return videoEl.webkitPresentationMode === "picture-in-picture";
};

/**
 * Request Picture-in-Picture for a video element. Returns a Promise so callers
 * can react to rejection (e.g. fall back to a full close); resolves immediately
 * on the iOS webkitSetPresentationMode path, which has no promise of its own.
 *
 * Must be called synchronously from inside a user gesture (e.g. a click
 * handler), and NOT awaited into before calling: iOS WebKit — especially
 * standalone PWAs — drops the "user gesture" context across an awaited
 * microtask boundary, which silently blocks both PiP APIs from doing anything.
 */
export const requestPiP = (videoEl) => {
  if (!videoEl) return Promise.reject(new Error("No video element"));
  if (document.pictureInPictureEnabled) {
    return videoEl.requestPictureInPicture();
  }
  if (videoEl.webkitSetPresentationMode) {
    videoEl.webkitSetPresentationMode("picture-in-picture");
  }
  return Promise.resolve();
};

/** Exit Picture-in-Picture, whichever API is currently active. */
export const exitPiP = (videoEl) => {
  if (document.pictureInPictureElement) {
    return document.exitPictureInPicture();
  }
  if (videoEl?.webkitPresentationMode === "picture-in-picture") {
    videoEl.webkitSetPresentationMode("inline");
  }
  return Promise.resolve();
};

/** Toggle Picture-in-Picture on/off for a video element. */
export const togglePiP = (videoEl) => {
  if (!videoEl) return Promise.resolve();
  return isPiPActive(videoEl) ? exitPiP(videoEl) : requestPiP(videoEl);
};
